import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Loader2, Trophy, TrendingUp, AlertTriangle, AlertOctagon, CheckCircle2, X, Save, Edit3 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS = [
  { v: 'on_track', label: 'On track', color: 'var(--good)', Icon: TrendingUp },
  { v: 'at_risk', label: 'At risk', color: 'var(--warn)', Icon: AlertTriangle },
  { v: 'off_track', label: 'Off track', color: 'var(--danger)', Icon: AlertOctagon },
  { v: 'achieved', label: 'Achieved', color: 'var(--accent)', Icon: CheckCircle2 },
  { v: 'missed', label: 'Missed', color: 'var(--text-low)', Icon: X },
];

function currentQuarter() {
  const n = new Date();
  return `${n.getFullYear()}-Q${Math.floor(n.getMonth()/3)+1}`;
}
function quarterOptions() {
  const n = new Date();
  const yr = n.getFullYear();
  const q = Math.floor(n.getMonth()/3)+1;
  const out = [];
  for (let i = -2; i <= 4; i++) {
    let qq = q + i, yy = yr;
    while (qq < 1) { qq += 4; yy -= 1; }
    while (qq > 4) { qq -= 4; yy += 1; }
    out.push(`${yy}-Q${qq}`);
  }
  return Array.from(new Set(out));
}

export default function GoalsPanel({ api }) {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quarter, setQuarter] = useState(currentQuarter());
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(empty());

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/ceo-life/goals', { params: { quarter } });
      setGoals(data || []);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  }, [api, quarter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const startCreate = () => { setEditing(null); setCreating(true); setDraft({ ...empty(), quarter }); };
  const startEdit = (g) => { setEditing(g); setCreating(false); setDraft({ ...g, key_results: g.key_results || [] }); };

  const save = async () => {
    if (!draft.title.trim()) { toast.error('Title required'); return; }
    try {
      if (draft.id) {
        const { data } = await api.put(`/ceo-life/goals/${draft.id}`, draft);
        setGoals(prev => prev.map(g => g.id === data.id ? data : g));
        toast.success('Updated');
      } else {
        const { data } = await api.post('/ceo-life/goals', draft);
        // Only push if quarter matches current view
        if (data.quarter === quarter) setGoals(prev => [data, ...prev]);
        toast.success('Goal added');
      }
      setEditing(null); setCreating(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      await api.delete(`/ceo-life/goals/${id}`);
      setGoals(prev => prev.filter(g => g.id !== id));
    } catch { toast.error('Failed'); }
  };

  const updateKR = async (goal, idx, patch) => {
    const next = (goal.key_results || []).map((k, i) => i === idx ? { ...k, ...patch } : k);
    try {
      const { data } = await api.put(`/ceo-life/goals/${goal.id}`, { key_results: next });
      setGoals(prev => prev.map(g => g.id === data.id ? data : g));
    } catch { toast.error('Failed'); }
  };

  const totalProgress = goals.length > 0 ? Math.round(goals.reduce((s, g) => s + (g.percent_done || 0), 0) / goals.length) : 0;

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>;

  return (
    <div className="space-y-4" data-testid="ceo-goals">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4" style={{ color: 'var(--gold)' }} />
          <select className="ceo-select" style={{ width: 140 }} value={quarter} onChange={e=>setQuarter(e.target.value)} data-testid="ceo-goals-quarter">
            {quarterOptions().map(q => <option key={q} value={q}>{q}</option>)}
          </select>
          {goals.length > 0 && (
            <span className="ceo-pill" style={{ fontSize: 11 }}>
              <span className="ceo-priority-dot" style={{ background: 'var(--accent)', boxShadow:'none' }} />
              {goals.length} goal{goals.length===1?'':'s'} · {totalProgress}% avg
            </span>
          )}
        </div>
        <button onClick={startCreate} className="ceo-btn ceo-btn-primary" data-testid="ceo-goal-new"><Plus className="h-3.5 w-3.5" />New goal</button>
      </div>

      {(creating || editing) && (
        <GoalForm draft={draft} setDraft={setDraft} onCancel={() => { setEditing(null); setCreating(false); }} onSave={save} />
      )}

      {goals.length === 0 && !creating && !editing ? (
        <div className="ceo-empty"><Trophy className="ceo-empty-icon h-9 w-9" /><p className="text-sm">No goals set for {quarter}. Set one to focus the quarter.</p></div>
      ) : (
        <div className="space-y-3">
          {goals.map(g => <GoalCard key={g.id} g={g} onEdit={() => startEdit(g)} onDelete={() => remove(g.id)} onUpdateKR={(idx, patch) => updateKR(g, idx, patch)} />)}
        </div>
      )}
    </div>
  );
}

function GoalCard({ g, onEdit, onDelete, onUpdateKR }) {
  const status = STATUS.find(s => s.v === g.status) || STATUS[0];
  const Sicon = status.Icon;
  return (
    <div className="ceo-card-elevated p-5" data-testid={`ceo-goal-${g.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-lg font-bold" style={{ color: 'var(--text)' }}>{g.title}</h3>
            <span className="ceo-pill" style={{ color: status.color, fontSize: 11 }}><Sicon className="h-2.5 w-2.5" />{status.label}</span>
            {g.owner && <span className="ceo-pill" style={{ fontSize: 10 }}>{g.owner}</span>}
          </div>
          {g.description && <p className="text-sm" style={{ color: 'var(--text-dim)' }}>{g.description}</p>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onEdit} className="ceo-btn ceo-btn-ghost" style={{ padding: '4px 6px' }}><Edit3 className="h-3 w-3" /></button>
          <button onClick={onDelete} className="ceo-btn ceo-btn-danger" style={{ padding: '4px 6px' }}><Trash2 className="h-3 w-3" /></button>
        </div>
      </div>

      {/* Overall progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="ceo-eyebrow">Overall</span>
          <span className="text-2xl font-bold" style={{ color: 'var(--text)', fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.03em' }}>{g.percent_done || 0}%</span>
        </div>
        <div style={{ height: 8, borderRadius: 4, background: 'var(--ink-3)', overflow: 'hidden' }}>
          <div style={{ width: `${g.percent_done || 0}%`, height: '100%', background: `linear-gradient(90deg, var(--accent-2), var(--accent), var(--gold))`, transition: 'width 400ms ease' }} />
        </div>
      </div>

      {/* Key Results */}
      {(g.key_results || []).length > 0 && (
        <div>
          <div className="ceo-eyebrow mb-2">Key results</div>
          <div className="space-y-2">
            {g.key_results.map((kr, i) => {
              const pct = kr.target > 0 ? Math.min(100, Math.round(kr.current * 100 / kr.target)) : (kr.done ? 100 : 0);
              return (
                <div key={kr.id||i} className="ceo-card p-3" data-testid={`ceo-kr-${g.id}-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <input type="checkbox" className="ceo-checkbox" checked={!!kr.done} onChange={e=>onUpdateKR(i, { done: e.target.checked })} />
                    <span className={`text-sm flex-1 ${kr.done?'line-through':''}`} style={{ color: kr.done?'var(--text-low)':'var(--text)' }}>{kr.title}</span>
                    {kr.target > 0 ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          className="ceo-input"
                          style={{ width: 70, textAlign: 'right', padding: '4px 8px' }}
                          value={kr.current}
                          onChange={e=>onUpdateKR(i, { current: parseFloat(e.target.value) || 0 })}
                          data-testid={`ceo-kr-current-${g.id}-${i}`}
                        />
                        <span className="text-xs" style={{ color: 'var(--text-low)' }}>/ {kr.target}{kr.unit?` ${kr.unit}`:''}</span>
                      </div>
                    ) : (
                      <span className="text-xs italic" style={{ color: 'var(--text-low)' }}>{kr.done ? 'Done' : 'Not done'}</span>
                    )}
                  </div>
                  {kr.target > 0 && (
                    <div style={{ height: 4, borderRadius: 2, background: 'var(--ink)', overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: pct >= 100 ? 'var(--good)' : 'var(--accent)', transition: 'width 300ms ease' }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function GoalForm({ draft, setDraft, onCancel, onSave }) {
  const set = (k, v) => setDraft(p => ({ ...p, [k]: v }));
  const setKR = (i, k, v) => set('key_results', (draft.key_results||[]).map((x, idx) => idx===i?{...x,[k]:v}:x));
  const addKR = () => set('key_results', [...(draft.key_results||[]), { id:`kr-${Date.now()}`, title:'', target:0, current:0, unit:'', done:false }]);
  const removeKR = (i) => set('key_results', (draft.key_results||[]).filter((_,idx)=>idx!==i));

  return (
    <div className="ceo-card-elevated p-5 space-y-3" data-testid="ceo-goal-form">
      <h3 className="ceo-headline text-xl">{draft.id ? 'Edit goal' : 'New goal'}</h3>
      <input className="ceo-input" placeholder="Goal title" autoFocus value={draft.title||''} onChange={e=>set('title',e.target.value)} data-testid="ceo-goal-title" />
      <textarea rows={2} className="ceo-textarea" placeholder="Why does this matter?" value={draft.description||''} onChange={e=>set('description',e.target.value)} />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <div><label className="ceo-eyebrow">Quarter</label>
          <select className="ceo-select" value={draft.quarter||currentQuarter()} onChange={e=>set('quarter',e.target.value)}>
            {quarterOptions().map(q => <option key={q} value={q}>{q}</option>)}
          </select>
        </div>
        <div><label className="ceo-eyebrow">Status</label>
          <select className="ceo-select" value={draft.status||'on_track'} onChange={e=>set('status',e.target.value)}>{STATUS.map(s=><option key={s.v} value={s.v}>{s.label}</option>)}</select>
        </div>
        <div><label className="ceo-eyebrow">Owner</label><input className="ceo-input" value={draft.owner||''} onChange={e=>set('owner',e.target.value)} /></div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="ceo-eyebrow">Key results</label>
          <button type="button" onClick={addKR} className="ceo-btn ceo-btn-ghost" style={{ padding:'3px 8px', fontSize:10 }}><Plus className="h-2.5 w-2.5" />Add KR</button>
        </div>
        {(draft.key_results||[]).map((kr, i) => (
          <div key={kr.id||i} className="grid grid-cols-12 gap-2 mb-2">
            <input className="ceo-input col-span-5" placeholder="Key result" value={kr.title||''} onChange={e=>setKR(i,'title',e.target.value)} />
            <input type="number" className="ceo-input col-span-2" placeholder="Target" value={kr.target||''} onChange={e=>setKR(i,'target',parseFloat(e.target.value)||0)} />
            <input type="number" className="ceo-input col-span-2" placeholder="Current" value={kr.current||''} onChange={e=>setKR(i,'current',parseFloat(e.target.value)||0)} />
            <input className="ceo-input col-span-2" placeholder="Unit" value={kr.unit||''} onChange={e=>setKR(i,'unit',e.target.value)} />
            <button type="button" onClick={()=>removeKR(i)} className="ceo-btn ceo-btn-danger col-span-1" style={{ padding: '0 8px' }}><X className="h-3 w-3" /></button>
          </div>
        ))}
        <p className="text-[10px]" style={{ color: 'var(--text-low)' }}>Leave Target at 0 for binary (done/not done) results.</p>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="ceo-btn ceo-btn-ghost"><X className="h-3 w-3" />Cancel</button>
        <button onClick={onSave} className="ceo-btn ceo-btn-primary" data-testid="ceo-goal-save"><Save className="h-3 w-3" />{draft.id?'Update':'Create goal'}</button>
      </div>
    </div>
  );
}

function empty() { return { title:'', description:'', quarter: currentQuarter(), status:'on_track', owner:'', key_results: [] }; }
